import { Injectable } from '@angular/core';
import { HelperService } from './helper.service';
import { of } from 'rxjs';
import { SigninService } from './signin/signin.service';
import { permissionsForFeature, permissionSlugs } from './utils/permission.utils';

export { permissionsForFeature, permissionSlugs };

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private user = null;
  private _token = null;
  public redirectUrl = null;
  private permissions = null;
  private authorizationPermissions: string[] = [];
  private authzPanels: string[] = [];
  private authorizationFetchFailed = false;

  setToken(token) {
    if (!token) {
      this._token = null;
      this.user = null;
      this.permissions = null;
      this.authorizationPermissions = [];
      return;
    }
    this._token = token;
  }

  get isUserLoggedIn() {
    return this.token ? true : false;
  }

  async fetchUserInfo() {
    try {
      const userDetails = await this.signInService.getUserInfo().toPromise();
      this.setUserDetails(userDetails);
      return userDetails;
    } catch (error) {
      const status = error && error.status;
      if (status == 401) {
        this.clearSession();
      }
      return null;
    }
  }

  get token() {
    return this._token;
  }

  constructor(
    private signInService: SigninService,
  ) { }

  /** Persist session on shared-parent-domain cookies (CSCC + payroll Google login). */
  public establishSession(token: string, permission?: any) {
    const normalized = HelperService.normalizeAuthToken(token);
    HelperService.setAuthCookies({ token: normalized, permission });
    this.setToken(normalized || null);
  }

  public getPermissionCookie(): string {
    return HelperService.accessCookie('permission') || '';
  }

  public clearSession() {
    this.setToken(null);
    HelperService.clearAuthCookies();
  }

  public async isAuthenticated(): Promise<boolean> {
    const token = this.getToken();
    this.setToken(token || null);

    if (!token) {
      return false;
    }

    await this.fetchUserInfo();

    // 401 clears the session; treat as unauthenticated so SSO cookies cannot stick.
    return !!(this.getToken() && this._token);
  }

  public clearUserCred() {
    this.clearSession();
  }

  public getUserDetails() {
    return of(this.user);
  }

  public setUserDetails(user) {
    this.permissions = user.permissions;
    this.authorizationPermissions = user.authorizationPermissions || [];
    this.authzPanels = user.authzPanels || [];
    this.authorizationFetchFailed = user.authorizationFetchFailed || false;
    this.user = user;
  }

  public getToken() {
    return HelperService.normalizeAuthToken(HelperService.accessCookie('token'));
  }

  public clearToken() {
    this.clearSession();
  }

  public async getPermissions() {
    if (!this.permissions) {
      await this.fetchUserInfo();
    }
    return this.permissions as string[];
  }

  public getAuthzPanels(): string[] {
    return this.authzPanels || [];
  }

  private getEffectivePermissions(feature?: string): string[] {
    const legacySlugs = this.normalizePermissionSlugs(this.permissions || []);
    const { effectivePermissions } = this.resolveFeatureAuthorization(feature, {
      authzPanels: this.authzPanels,
      legacyPermissions: legacySlugs,
      authorizationPermissions: this.authorizationPermissions,
    });
    return effectivePermissions;
  }

  public normalizePermissionSlugs(permissions: any): string[] {
    return (permissions || []).map((p) => {
      if (typeof p === 'string') {
        return p;
      }
      return (p && (p.name || p.slug)) || '';
    }).filter(Boolean);
  }

  public isAuthorizedFor(permission?: string | string[], feature?: string): boolean {
    const required = Array.isArray(permission) ? permission.filter(Boolean) : (permission ? [permission] : []);
    if (!required.length) {
      return true;
    }
    const effectivePermissions = this.getEffectivePermissions(feature);
    if (!effectivePermissions || !effectivePermissions.length) {
      return false;
    }
    const userPermissionSlugs = this.normalizePermissionSlugs(effectivePermissions);
    return required.some((slug) => userPermissionSlugs.includes(slug));
  }

  private resolveFeatureAuthorization(
    feature: string | undefined,
    options: {
      authzPanels?: string[];
      legacyPermissions?: string[];
      authorizationPermissions?: string[];
    } = {}
  ) {
      const authzPanels = options.authzPanels || [];
      const legacyPermissions = options.legacyPermissions || [];
      const authorizationPermissions = options.authorizationPermissions || [];
      const isAuthorizationPanel = Boolean(feature && authzPanels.includes(feature));

      if (!isAuthorizationPanel) {
        return {
          effectivePermissions: legacyPermissions,
          isAuthorizationPanel: false,
        };
      }

      // If resource requires AUTHZ flow but authorization fetch failed, throw error instead of falling back
      if (this.authorizationFetchFailed) {
        throw new Error(`Authorization service fetch failed for feature: ${feature}. Cannot determine access.`);
      }

      return {
        effectivePermissions: authorizationPermissions,
        isAuthorizationPanel: true,
      };
    }
}
